/**
 * Modal Component - Reusable modal with fixed header and footer
 * Based on shadcn Dialog with customizations for SisPDV
 * 
 * Usage (Simple API):
 * <Modal 
 *   open={open} 
 *   onOpenChange={setOpen}
 *   title="Título do Modal"
 *   description="Descrição opcional"
 *   size="lg"
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   confirmText="Salvar"
 *   cancelText="Cancelar"
 *   loading={saving}
 *   disableConfirm={!isValid}
 * >
 *   {content}
 * </Modal>
 * 
 * Usage (Advanced - Custom Footer):
 * <Modal open={open} onOpenChange={setOpen} title="Título" footer={<CustomFooter />}>
 *   {content}
 * </Modal>
 */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

// Base Radix components
const ModalRoot = DialogPrimitive.Root;
const ModalTrigger = DialogPrimitive.Trigger;
const ModalPortal = DialogPrimitive.Portal;
const ModalClose = DialogPrimitive.Close;

const ModalOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
ModalOverlay.displayName = 'ModalOverlay';

const sizeClasses = {
  sm: 'max-w-md',
  default: 'max-w-lg',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-5xl',
  full: 'max-w-[95vw]',
};

const ModalContent = React.forwardRef(({ className, children, size = 'default', showCloseButton = true, ...props }, ref) => (
  <ModalPortal>
    <ModalOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]',
        'w-full p-0',
        sizeClasses[size] || sizeClasses.default,
        'max-h-[90vh] flex flex-col',
        'bg-background border rounded-xl shadow-2xl',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'duration-200',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <ModalClose className="absolute right-4 top-4 rounded-full p-1.5 opacity-70 transition-all hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none z-10">
          <X className="h-4 w-4" />
          <span className="sr-only">Fechar</span>
        </ModalClose>
      )}
    </DialogPrimitive.Content>
  </ModalPortal>
));
ModalContent.displayName = 'ModalContent';

const ModalHeader = ({ className, ...props }) => (
  <div
    className={cn(
      'flex flex-col gap-1.5 p-6 pb-4',
      'border-b bg-muted/30',
      'flex-shrink-0',
      className
    )}
    {...props}
  />
);
ModalHeader.displayName = 'ModalHeader';

const ModalBody = ({ className, children, ...props }) => {
  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto p-6',
        'min-h-0',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};
ModalBody.displayName = 'ModalBody';

const ModalFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 px-6',
      'border-t bg-muted/30',
      'flex-shrink-0',
      className
    )}
    {...props}
  />
);
ModalFooter.displayName = 'ModalFooter';

const ModalTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
ModalTitle.displayName = 'ModalTitle';

const ModalDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
ModalDescription.displayName = 'ModalDescription';

/**
 * StandardModal - Simplified modal with built-in header and footer
 * 
 * @param {boolean} open - Whether modal is open
 * @param {function} onOpenChange - Callback when open state changes
 * @param {string} title - Modal title (required)
 * @param {string} description - Optional description below title
 * @param {string} size - Modal size: 'sm' | 'default' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
 * @param {ReactNode} children - Modal body content
 * @param {ReactNode|null} footer - Custom footer (set to null to hide default, or pass custom content)
 * @param {function} onConfirm - Callback for confirm button
 * @param {function} onCancel - Callback for cancel button
 * @param {function} onClose - Callback when modal closes (by any method)
 * @param {string} confirmText - Text for confirm button (default: "Confirmar")
 * @param {string} cancelText - Text for cancel button (default: "Cancelar")
 * @param {boolean} loading - Show loading state on confirm button
 * @param {boolean} showCancel - Show cancel button (default: true)
 * @param {boolean} showConfirm - Show confirm button (default: true)
 * @param {string} confirmVariant - Button variant for confirm (default: "default")
 * @param {boolean} disableConfirm - Disable confirm button
 * @param {string} className - Additional className for modal content
 */
const Modal = ({
  open,
  onOpenChange,
  title,
  description,
  size = 'default',
  children,
  footer,
  onConfirm,
  onCancel,
  onClose,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
  showCancel = true,
  showConfirm = true,
  confirmVariant = 'default',
  disableConfirm = false,
  className,
  ...props
}) => {
  // Handle close with callback
  const handleOpenChange = (newOpen) => {
    if (!newOpen && onClose) {
      onClose();
    }
    onOpenChange(newOpen);
  };

  // Handle cancel with callback
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  // Handle confirm with callback
  const handleConfirm = async () => {
    if (onConfirm) {
      const result = onConfirm();
      // If onConfirm returns a promise, wait for it
      if (result instanceof Promise) {
        await result;
      }
    }
    // Note: onConfirm should handle closing if needed
    // This allows for validation before closing
  };

  // Determine if we should show default footer
  const showDefaultFooter = footer === undefined && (showCancel || showConfirm);

  return (
    <ModalRoot open={open} onOpenChange={handleOpenChange}>
      <ModalContent size={size} className={className} {...props}>
        {/* Fixed Header */}
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          {description && <ModalDescription>{description}</ModalDescription>}
        </ModalHeader>

        {/* Scrollable Body */}
        <ModalBody>
          {children}
        </ModalBody>

        {/* Fixed Footer */}
        {footer !== null && (
          footer !== undefined ? (
            // Custom footer
            <ModalFooter>{footer}</ModalFooter>
          ) : showDefaultFooter ? (
            // Default footer with buttons
            <ModalFooter>
              {showCancel && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={loading}
                  data-testid="modal-cancel-btn"
                >
                  {cancelText}
                </Button>
              )}
              {showConfirm && (
                <Button 
                  type="button"
                  variant={confirmVariant}
                  onClick={handleConfirm}
                  disabled={loading || disableConfirm}
                  data-testid="modal-confirm-btn"
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {confirmText}
                </Button>
              )}
            </ModalFooter>
          ) : null
        )}
      </ModalContent>
    </ModalRoot>
  );
};

// Also export individual components for advanced usage
export {
  Modal,
  ModalRoot,
  ModalPortal,
  ModalOverlay,
  ModalClose,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
  ModalDescription,
};
